import { reverse } from 'named-urls'
import React, { useMemo } from 'react'
import { type Location, useLocation } from 'react-router-dom'

import { ButtonClean, useModalContext } from '@fourthwall/components'
import { TransactionSummaryModal } from '@modules/Dashboard/pages/Settings/pages/Billing/pages/Transactions/components/TransactionSummaryModal/TransactionSummaryModal'
import type { FromLocationState } from '@types'
import { routing } from '@utils/routing'

import type { Transaction } from '../../Transactions.types'

export type Props = {
  transaction: Transaction
}

export const TransactionIdLink = ({ transaction }: Props) => {
  const location = useLocation()

  const getDonationPath = (donationId: string) => {
    return reverse(routing.contributions.orders.donations.donation.self, { donationId })
  }

  const getOrderPath = (orderId: string) => {
    return reverse(routing.contributions.orders.order.self, { orderId })
  }

  const pathNames: Partial<
    Record<Transaction['type'], (id: Transaction['originalId']) => string | undefined>
  > = useMemo(() => {
    return {
      DONATION: (donationId) => {
        return donationId && getDonationPath(donationId)
      },
      DONATION_CANCEL: (donationId) => {
        return donationId && getDonationPath(donationId)
      },
      ORDER: (orderId) => {
        return orderId && getOrderPath(orderId)
      },
      ORDER_REFUND: (orderId) => {
        return orderId && getOrderPath(orderId)
      },
      ORDER_CANCEL: (orderId) => {
        return orderId && getOrderPath(orderId)
      },
      ORDER_PARTIAL_CANCEL: (orderId) => {
        return orderId && getOrderPath(orderId)
      },
      INVENTORY_SHIPMENT: (shippingId) => {
        return (
          shippingId && reverse(routing.settings.shipping.inventoryShipments.self, { shippingId })
        )
      },
      INVENTORY_STORAGE: (snapshotId) => {
        return snapshotId && reverse(routing.settings.shipping.inventoryStocks.self, { snapshotId })
      },
    }
  }, [])

  const getFromState = (location: Location): FromLocationState => {
    return { from: { label: 'Earnings history', location } }
  }

  const getPathname = ({ type, originalId }: Transaction): string | undefined => {
    const pathName = pathNames[type]

    return pathName && pathName(originalId)
  }

  const getButtonProps = (transaction: Transaction, location: Location) => {
    const { open, close } = useModalContext()

    const pathname = getPathname(transaction)

    if (pathname) {
      return { to: { pathname, state: getFromState(location) } }
    }

    return { onClick: () => open(TransactionSummaryModal, { transaction, onClose: close }) }
  }

  return <ButtonClean label={`#${transaction.id}`} {...getButtonProps(transaction, location)} />
}
